/**
 * authPaths — single source of truth for "which door does this person
 * belong to?". Wave 12.x security separation.
 *
 * The app has THREE doors:
 *   • Public marketing site  → `/`
 *   • Owner / Advisor door   → `/owner-access`  (advisor + owner + developer)
 *   • Client / Member door   → per-suite `/suite/{slug}/access`. When the
 *     specific slug isn't known yet, we send the user to the NEUTRAL
 *     `/access` page rather than defaulting to any particular family.
 *
 * The legacy unified `/login` route has been killed. All redirects that
 * used to land on `/login` are now routed through this helper so the
 * client never sees advisor language and vice-versa.
 *
 * Feb 2026 — Removed the hardcoded "/suite/jackson/access" fallback.
 * Defaulting every unknown client redirect to Jackson would have leaked
 * one family's branding to other families.
 */

const ADVISOR_DOOR = "/owner-access";
const NEUTRAL_CLIENT_DOOR = "/access";

/**
 * Extract the suite slug from a path like `/suite/jackson/...` or
 * `/portal/client/jackson/...` if present.  Returns null when no slug
 * can be derived — the caller should then fall back to the NEUTRAL door.
 */
function slugFromPath(pathname) {
  const p = (pathname || "").toLowerCase();
  const m = p.match(/^\/suite\/([a-z0-9-]+)(?:\/|$)/);
  if (m && m[1] && m[1] !== "jackson") return m[1];
  if (m && m[1] === "jackson") return "jackson";
  return null;
}

/**
 * Return the correct sign-in door for a given role.
 * Anything that is not explicitly `client` is treated as advisor/owner.
 * Clients land on the neutral door — only path-context can resolve a
 * specific suite (see loginPathForPath below).
 */
export function loginPathForRole(role) {
  if ((role || "").toLowerCase() === "client") return NEUTRAL_CLIENT_DOOR;
  return ADVISOR_DOOR;
}

/**
 * Path-aware fallback used by the reset/recovery flow and protected
 * routes. If the current path already names a suite slug, route back
 * to that suite's access page. Otherwise:
 *   • client paths with no slug → neutral access page
 *   • all other paths           → advisor door
 */
export function loginPathForPath(pathname) {
  const p = (pathname || "").toLowerCase();
  const slug = slugFromPath(p);
  if (slug) return `/suite/${slug}/access`;
  if (p.startsWith("/portal/client") || p.startsWith("/suite/")) {
    return NEUTRAL_CLIENT_DOOR;
  }
  return ADVISOR_DOOR;
}

export const ADVISOR_LOGIN_PATH = ADVISOR_DOOR;
export const CLIENT_LOGIN_PATH = NEUTRAL_CLIENT_DOOR;
